import type { AddReturnInput, EstimateItineraryInput } from '@allocab/allocab-booking-service-types';
import { PageableInput, PassengerSearchInput } from '@allocab/allocab-commons-shared-types';
import { DriverSuggestionInput } from '@allocab/allocab-driver-service-types';
import { STORY_MISSION_TYPE } from '@allocab/allocab-driver-shared-types';
import { InvoiceSearchInput } from '@allocab/allocab-invoice-service-types';
import type {
  StatsChannelInput,
  StatsByOrchestrationId,
  StatsOrchestrationInput,
  StatsOrchestrationTripsInput,
} from '@allocab/allocab-marketing-data-service-types';
import {
  ListInvitationsInput,
  CustomRulesInput,
  PaymentModeAvailableInput,
  SearchTripV2Input,
  GetInvitationInput,
  ListInvoicesInput,
  GetPathInput,
  InitWalletPaymentInput,
  InitPaymentIntentInput,
  DriverAroundInput,
  FindPoiInput,
} from '@allocab/allocab-passenger-shared-types';
import { MongoSearchInput, TimeRangeQueryInput } from '@allocab/allocab-standard-shared-types';
import type { FindSuggestionInput } from '@allocab/allocab-suggestion-types';
import {
  AirlineSearchAutocompleteInput,
  FindTrainInput,
  GetFlightInput,
  ScheduledArrivalsInput,
} from '@allocab/allocab-transport-management-types';
import { SearchTravelPoliciesWithoutKeyInput } from '@allocab/allocab-travel-policy-service-types';
import { GetMissionInput } from '@allocab/allocab-trip-service-types';
import { SearchPassengerAccountInput, SearchUserAccountInput } from '@allocab/allocab-user-service-types';

export const queryKeys = {
  root: () => ['root'],
  tokens: () => [...queryKeys.root(), 'tokens'],
  fetchPickUpGuide: (tripKey: string) => ['fetchPickUpGuide', tripKey],
  fetchDriversAround: (input: DriverAroundInput | null) => ['fetchDriversAround', input],
  fetchRemoteConfig: () => ['fetchRemoteConfig'],
  passenger: {
    root: () => [...queryKeys.root(), 'passenger'],
    me: () => [...queryKeys.passenger.root(), 'me'],
    fetchAccount: () => [...queryKeys.passenger.root(), 'fetchAccount'],
    phoneMessage: () => [...queryKeys.passenger.root(), 'phoneMessage'],
    fetchTrainById: (trainId: string, stationUic: string) => [
      ...queryKeys.passenger.root(),
      'fetchTrainById',
      trainId,
      stationUic,
    ],
    fetchAirlines: (input: AirlineSearchAutocompleteInput) => [...queryKeys.passenger.root(), 'fetchAirlines', input],
    fetchFlightSchedule: (input: GetFlightInput) => [...queryKeys.passenger.root(), 'fetchFlightSchedule', input],
    fetchPoiByPec: (input: FindPoiInput) => [...queryKeys.passenger.root(), 'fetchPoiByPec', input],
    fetchTrainSchedule: (
      input: Pick<FindTrainInput, 'startDate' | 'endDate'> & { trainNumber: string; stationUic: string },
    ) => [...queryKeys.passenger.root(), 'fetchTrainSchedule', input],
    fetchTrainScheduleByStation: (input: Pick<FindTrainInput, 'startDate' | 'endDate'> & { stationUic: string }) => [
      ...queryKeys.passenger.root(),
      'fetchTrainScheduleByStation',
      input,
    ],
    fetchFlightScheduleByAirport: (input: ScheduledArrivalsInput) => [
      ...queryKeys.passenger.root(),
      'fetchFlightScheduleByAirport',
      input,
    ],
    fetchItineraryEstimate: (input: EstimateItineraryInput) => [
      ...queryKeys.passenger.root(),
      'fetchItineraryEstimate',
      input,
    ],
    fetchAuthorizeWalletPayment: (input: InitWalletPaymentInput) => [
      ...queryKeys.passenger.root(),
      'fetchAuthorizeWalletPayment',
      input,
    ],
    fetchCartJourneys: (key: string) => [...queryKeys.passenger.root(), 'fetchCartJourneys', key],
    fetchPec: (id?: string) => [...queryKeys.passenger.root(), 'fetchPec', id],
    fetchPoiByPecId: (id?: string) => [...queryKeys.passenger.root(), 'fetchPoiByPecId', id],
    fetchCart: (key: string) => [...queryKeys.passenger.root(), 'fetchCart', key],
    fetchJourney: (key: string) => [...queryKeys.passenger.root(), 'fetchJourney', key],
    fetchJourneyItems: (key?: string) => [...queryKeys.passenger.root(), 'fetchJourneyItems', ...(key ? [key] : [])],
    fetchJoinAccountLink: () => [...queryKeys.passenger.root(), 'fetchJoinAccountLink'],
    fetchEstimateReturn: (journeyKey: string, input: AddReturnInput) => [
      ...queryKeys.passenger.root(),
      'fetchEstimateReturn',
      journeyKey,
      input,
    ],
    fetchRecentlyUsedAddresses: () => [...queryKeys.passenger.root(), 'fetchRecentlyUsedAddresses'],
    fetchInitPaymentIntent: (input?: InitPaymentIntentInput & { cartAmount?: number }) => [
      ...queryKeys.passenger.root(),
      'fetchInitPaymentIntent',
      ...(input ? [input] : []),
    ],
    fetchTravelPolicies: (input?: Omit<SearchTravelPoliciesWithoutKeyInput, 'offset' | 'limit'>) => [
      ...queryKeys.passenger.root(),
      'fetchTravelPolicies',
      ...(input ? [input] : []),
    ],
    fetchInvitations: (input?: ListInvitationsInput) => [
      ...queryKeys.passenger.root(),
      'fetchInvitations',
      ...(input ? [input] : []),
    ],
    trips: (input?: Omit<SearchTripV2Input, 'offset' | 'limit'>) => [
      ...queryKeys.passenger.root(),
      'trips',
      ...(input ? [input] : []),
    ],
    driverPath: (input?: GetPathInput) => [...queryKeys.passenger.root(), 'driverPath', ...(input ? [input] : [])],
    path: (input?: GetPathInput) => [...queryKeys.passenger.root(), 'path', ...(input ? [input] : [])],
    trip: (tripKey: string) => [...queryKeys.passenger.root(), 'trip', tripKey],
    referrals: () => [...queryKeys.passenger.root(), 'referrals'],
    passengers: (input?: PassengerSearchInput) => [
      ...queryKeys.passenger.root(),
      'passengers',
      ...(input ? [input] : []),
    ],
    fetchTripUpdateRequest: (id: string) => [...queryKeys.passenger.root(), 'fetchTripUpdateRequest', id],
    fetchTravelPolicy: (id: string) => [...queryKeys.passenger.root(), 'fetchTravelPolicy', id],
    passenger: (key: string) => [...queryKeys.passenger.root(), 'passenger', key],
    fetchCollaborator: (key: string) => [...queryKeys.passenger.root(), 'fetchCollaborator', key],
    collaborators: (input?: PageableInput) => [
      ...queryKeys.passenger.root(),
      'collaborators',
      ...(input ? [input] : []),
    ],
    addresses: () => [...queryKeys.passenger.root(), 'addresses'],
    address: (id: string) => [...queryKeys.passenger.root(), 'address', id],
    payments: (input?: PaymentModeAvailableInput) => [
      ...queryKeys.passenger.root(),
      'payments',
      ...(input ? [input] : []),
    ],
    payment: (key: string) => [...queryKeys.passenger.root(), 'payment', key],
    transport: (tripId: string) => [...queryKeys.passenger.root(), 'transport', tripId],
    customRules: (input: CustomRulesInput) => [...queryKeys.passenger.root(), 'customRules', input],
    fetchInvoices: (input?: ListInvoicesInput) => [
      ...queryKeys.passenger.root(),
      'fetchInvoices',
      ...(input ? [input] : []),
    ],
    fetchInvoicesLegacy: (input?: ListInvoicesInput) => [
      ...queryKeys.passenger.root(),
      'fetchInvoicesLegacy',
      ...(input ? [input] : []),
    ],
    canUpdateTrip: (tripKey: string) => [...queryKeys.passenger.root(), 'canUpdateTrip', tripKey],
    fetchSuggestions: (input: FindSuggestionInput | null) => [...queryKeys.passenger.root(), 'fetchSuggestions', input],
    fetchInvitation: (input?: GetInvitationInput) => [
      ...queryKeys.passenger.root(),
      'fetchInvitation',
      ...(input ? [input] : []),
    ],
    fetchStats: (input?: TimeRangeQueryInput) => [
      ...queryKeys.passenger.root(),
      'fetchStats',
      ...(input ? [input] : []),
    ],
    fetchStatsAccount: (input?: TimeRangeQueryInput) => [
      ...queryKeys.passenger.root(),
      'fetchStatsAccount',
      ...(input ? [input] : []),
    ],
  },
  bo: {
    root: () => [...queryKeys.root(), 'bo'],
    fetchOperatorMe: () => [...queryKeys.bo.root(), 'fetchOperatorMe'],
    fetchMarketingDataOrchestration: (input: StatsOrchestrationInput) => [
      ...queryKeys.bo.root(),
      'fetchMarketingDataOrchestration',
      input,
    ],
    fetchMarketingDataChannel: (input: StatsChannelInput) => ['fetchMarketingDataChannel', input],
    fetchPassengerAccounts: (input: SearchPassengerAccountInput) => [
      ...queryKeys.bo.root(),
      'fetchPassengerAccounts',
      input,
    ],
    fetchDriversByName: (input: DriverSuggestionInput) => [...queryKeys.bo.root(), 'fetchDriversByName', input],
    fetchTrips: (input: MongoSearchInput) => [...queryKeys.bo.root(), 'fetchTrips', input],
    fetchUsers: (input: SearchUserAccountInput) => [...queryKeys.bo.root(), 'fetchUsers', input],
    fetchDriverInvoices: (input: InvoiceSearchInput) => [...queryKeys.bo.root(), 'fetchDriverInvoices', input],
    fetchMarketingDataOrchestrationStats: (orchestrationId: string, input: StatsByOrchestrationId) => [
      ...queryKeys.bo.root(),
      'fetchMarketingDataOrchestrationStats',
      orchestrationId,
      input,
    ],
    fetchMarketingDataOrchestrationById: (orchestrationId: string, input: StatsByOrchestrationId) => [
      ...queryKeys.bo.root(),
      'fetchMarketingDataOrchestrationById',
      orchestrationId,
      input,
    ],
    fetchMarketingDataOrchestrationTrips: (orchestrationId: string, input: StatsOrchestrationTripsInput) => [
      ...queryKeys.bo.root(),
      'fetchMarketingDataOrchestrationTrips',
      orchestrationId,
      input,
    ],
    fetchMarketingDataOrchestrationSteps: (orchestrationId: string, input: StatsByOrchestrationId) => [
      ...queryKeys.bo.root(),
      'fetchMarketingDataOrchestrationSteps',
      orchestrationId,
      input,
    ],
    fetchDriverMissions: (input: GetMissionInput) => [...queryKeys.bo.root(), 'fetchDriverMissions', input],
    fetchDriverMissionById: (id: string) => [...queryKeys.bo.root(), 'fetchDriverMissionById', id],
    fetchDriverLeadById: (id: string) => [...queryKeys.bo.root(), 'fetchDriverLeadById', id],
    fetchDriverMissionFiles: (id: string) => [...queryKeys.bo.root(), 'fetchDriverMissionFiles', id],
    updateDriverMissionStatus: (id: string) => [...queryKeys.bo.root(), 'updateDriverMissionStatus', id],
  },
  driver: {
    root: () => [...queryKeys.root(), 'driver'],
    fetchDriverMission: () => [...queryKeys.driver.root(), 'fetchDriverMission'],
    fetchDriverMissionTrip: () => [...queryKeys.driver.root(), 'fetchDriverMissionTrip'],
    fetchDriverMissionVehicle: (licensePlate: string) => [
      ...queryKeys.driver.root(),
      'fetchDriverMissionVehicle',
      licensePlate,
    ],
    fetchFileStoriesByOwner: (ownerId: string) => [...queryKeys.driver.root(), 'fetchFileStoriesByOwner', ownerId],
    fetchFiles: (input: { type: STORY_MISSION_TYPE | 'RIB' }) => [...queryKeys.driver.root(), 'fetchFiles', input],
  },
};
